import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';

export type SidenavWidth = 'default' | 'large';

export interface PanelOptions {
  width?: SidenavWidth;
}

interface SidenavPanel {
  portal: ComponentPortal<any>;
  width: SidenavWidth;
}

/**
 * The panels shown in the form sidenav, as a stack: only the top one is visible, and the ones under it
 * stay alive, form state included, until they are revealed again.
 *
 * - `openPortal` shows a panel on its own, replacing whatever was open.
 * - `pushPortal` opens a panel on top of the current one, e.g. to edit one item of the form below it.
 *   Its opener passes callbacks that end in `popPortal()`, which reveals the panel underneath.
 * - `closePortal` closes them all.
 *
 * The sidenav takes the `width` of the top panel; `large` is only wider on desktop screens.
 */
@Injectable({
  providedIn: 'root',
})
export class SidenavPortalBridgeService {
  private stack = new BehaviorSubject<SidenavPanel[]>([]);

  readonly portals$ = this.stack.pipe(
    map((panels) => panels.map((p) => p.portal)),
  );

  readonly width$ = this.stack.pipe(
    map((panels) => panels.at(-1)?.width ?? 'default'),
    distinctUntilChanged(),
  );

  constructor(private injector: Injector) {}

  /** Whether a panel is showing, so that one about to be pushed knows whether it covers another. */
  get isOpen(): boolean {
    return this.stack.value.length > 0;
  }

  openPortal<T>(
    component: any,
    DATA_TOKEN: InjectionToken<T>,
    args: T,
    options: PanelOptions = {},
  ) {
    this.stack.next([this.createPanel(component, DATA_TOKEN, args, options)]);
  }

  pushPortal<T>(
    component: any,
    DATA_TOKEN: InjectionToken<T>,
    args: T,
    options: PanelOptions = {},
  ) {
    this.stack.next([
      ...this.stack.value,
      this.createPanel(component, DATA_TOKEN, args, options),
    ]);
  }

  /** Closes the top panel, revealing the one under it; closing the last one closes the sidenav. */
  popPortal() {
    this.stack.next(this.stack.value.slice(0, -1));
  }

  /**
   * The portal outlet is the form sidenav, a sibling of the router outlet rather than
   * a part of it, so navigating away does not take an open panel with it. A component
   * that opens a portal has to close it in its own ngOnDestroy; nothing else will.
   */
  closePortal() {
    this.stack.next([]);
  }

  private createPanel<T>(
    component: any,
    DATA_TOKEN: InjectionToken<T>,
    args: T,
    { width = 'default' }: PanelOptions,
  ): SidenavPanel {
    const portalInjector = Injector.create({
      providers: [{ provide: DATA_TOKEN, useValue: args }],
      parent: this.injector,
    });
    return {
      portal: new ComponentPortal(component, null, portalInjector),
      width,
    };
  }
}
